/**
 * Compile Gate View Model Hook
 * 
 * Manages filtering, sorting, grouping, selection, reviewed/accepted states, and export.
 */

import { useState, useMemo, useCallback } from 'react';

export interface Violation {
  rule_id: string;
  rule_name: string;
  severity: string;
  status: string;
  message: string;
  activity_id?: string;
  activity_name?: string;
  fix_suggestion?: string;
  requires_approval: boolean;
  approval_owner?: string;
  wbs_path?: string;
  wbs_code?: string;
}

export type GroupByMode = 'none' | 'rule' | 'severity' | 'wbs';

interface ViewState {
  searchQuery: string;
  selectedSeverities: string[];
  selectedRules: string[];
  groupBy: GroupByMode;
  showReviewed: boolean;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  compactMode: boolean;
  reviewMode: boolean; // Toggle review functionality on/off
}

interface ViolationState {
  reviewed: Set<string>;
  accepted: Set<string>;
  selected: Set<string>;
}

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low'];
const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3
};

export function useCompileGateViewModel(violations: Violation[]) {
  const [viewState, setViewState] = useState<ViewState>({
    searchQuery: '',
    selectedSeverities: [],
    selectedRules: [],
    groupBy: 'rule', // Default to rule grouping for better organization
    showReviewed: false,
    sortColumn: 'severity',
    sortDirection: 'desc',
    compactMode: false,
    reviewMode: true // Review mode enabled by default
  });

  const [violationState, setViolationState] = useState<ViolationState>({
    reviewed: new Set(),
    accepted: new Set(),
    selected: new Set()
  });

  // Generate stable violation ID
  const getViolationId = useCallback((v: Violation, index: number) => {
    return `${v.rule_id}-${v.activity_id || 'no-activity'}-${index}`;
  }, []);

  // Filter violations
  const filteredViolations = useMemo(() => {
    let filtered = [...violations];

    // Search filter
    if (viewState.searchQuery) {
      const query = viewState.searchQuery.toLowerCase();
      filtered = filtered.filter(v =>
        v.rule_name.toLowerCase().includes(query) ||
        v.message.toLowerCase().includes(query) ||
        (v.activity_name && v.activity_name.toLowerCase().includes(query)) ||
        (v.activity_id && v.activity_id.toLowerCase().includes(query))
      );
    }

    // Severity filter
    if (viewState.selectedSeverities.length > 0) {
      filtered = filtered.filter(v =>
        viewState.selectedSeverities.includes(v.severity.toLowerCase())
      );
    }

    // Rule filter
    if (viewState.selectedRules.length > 0) {
      filtered = filtered.filter(v =>
        viewState.selectedRules.includes(v.rule_id)
      );
    }

    // Reviewed filter
    if (!viewState.showReviewed) {
      filtered = filtered.filter((v, idx) => {
        const id = getViolationId(v, idx);
        return !violationState.reviewed.has(id);
      });
    }

    return filtered;
  }, [violations, viewState, violationState.reviewed, getViolationId]);

  // Sort violations
  const sortedViolations = useMemo(() => {
    const sorted = [...filteredViolations];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (viewState.sortColumn) {
        case 'severity':
          comparison = (SEVERITY_RANK[a.severity.toLowerCase()] ?? 99) -
                      (SEVERITY_RANK[b.severity.toLowerCase()] ?? 99);
          break;
        case 'activity_id': {
          const aId = a.activity_id || '';
          const bId = b.activity_id || '';
          comparison = aId.localeCompare(bId);
          break;
        }
        case 'activity_name': {
          const aName = a.activity_name || '';
          const bName = b.activity_name || '';
          comparison = aName.localeCompare(bName);
          break;
        }
        case 'rule':
          comparison = a.rule_name.localeCompare(b.rule_name);
          break;
        case 'message':
          comparison = a.message.localeCompare(b.message);
          break;
        default:
          comparison = 0;
      }

      if (comparison === 0) {
        // Secondary sort: severity, then rule, then activity_id
        const severityComp = (SEVERITY_RANK[a.severity.toLowerCase()] ?? 99) -
                            (SEVERITY_RANK[b.severity.toLowerCase()] ?? 99);
        if (severityComp !== 0) return severityComp;
        
        const ruleComp = a.rule_name.localeCompare(b.rule_name);
        if (ruleComp !== 0) return ruleComp;
        
        return (a.activity_id || '').localeCompare(b.activity_id || '');
      }

      return viewState.sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredViolations, viewState.sortColumn, viewState.sortDirection]);

  // Group violations
  const groupedViolations = useMemo(() => {
    if (viewState.groupBy === 'none') {
      return { '': sortedViolations };
    }

    const groups: Record<string, Violation[]> = {};

    sortedViolations.forEach(v => {
      let key = '';
      switch (viewState.groupBy) {
        case 'rule':
          key = v.rule_id;
          break;
        case 'severity':
          key = v.severity.toLowerCase();
          break;
        case 'wbs':
          key = v.wbs_path || v.wbs_code || 'No WBS';
          break;
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(v);
    });

    return groups;
  }, [sortedViolations, viewState.groupBy]);

  // Flatten grouped violations for virtualization
  const flattenedRows = useMemo(() => {
    const rows: Array<{ type: 'group' | 'violation'; key: string; data: any }> = [];

    Object.entries(groupedViolations).forEach(([groupKey, groupViolations]) => {
      if (viewState.groupBy !== 'none') {
        rows.push({
          type: 'group',
          key: `group-${groupKey}`,
          data: {
            key: groupKey,
            label: groupKey,
            count: groupViolations.length,
            violations: groupViolations
          }
        });
      }

      groupViolations.forEach((v, idx) => {
        rows.push({
          type: 'violation',
          key: getViolationId(v, idx),
          data: v
        });
      });
    });

    return rows;
  }, [groupedViolations, viewState.groupBy, getViolationId]);

  // Get unique values for filters
  const uniqueSeverities = useMemo(() => {
    return Array.from(new Set(violations.map(v => v.severity.toLowerCase()))).sort();
  }, [violations]);

  const uniqueRules = useMemo(() => {
    // Use a Map to ensure unique rules by rule_id
    const ruleMap = new Map<string, { id: string; name: string }>();
    violations.forEach(v => {
      if (!ruleMap.has(v.rule_id)) {
        ruleMap.set(v.rule_id, { id: v.rule_id, name: v.rule_name });
      }
    });
    return Array.from(ruleMap.values())
      .sort((a, b) => {
        const nameComp = a.name.localeCompare(b.name);
        return nameComp !== 0 ? nameComp : a.id.localeCompare(b.id);
      });
  }, [violations]);

  // Actions
  const toggleReviewed = useCallback((violationId: string) => {
    setViolationState(prev => {
      const newReviewed = new Set(prev.reviewed);
      if (newReviewed.has(violationId)) {
        newReviewed.delete(violationId);
      } else {
        newReviewed.add(violationId);
      }
      return { ...prev, reviewed: newReviewed };
    });
  }, []);

  const toggleSelected = useCallback((violationId: string) => {
    setViolationState(prev => {
      const newSelected = new Set(prev.selected);
      if (newSelected.has(violationId)) {
        newSelected.delete(violationId);
      } else {
        newSelected.add(violationId);
      }
      return { ...prev, selected: newSelected };
    });
  }, []);

  const bulkMarkReviewed = useCallback((violationIds: string[]) => {
    setViolationState(prev => {
      const newReviewed = new Set(prev.reviewed);
      violationIds.forEach(id => newReviewed.add(id));
      return { ...prev, reviewed: newReviewed };
    });
  }, []);

  const acceptRisk = useCallback((violationId: string, reason: string) => {
    setViolationState(prev => {
      const newAccepted = new Set(prev.accepted);
      newAccepted.add(violationId);
      return { ...prev, accepted: newAccepted };
    });
  }, []);

  const selectAll = useCallback(() => {
    const allIds = flattenedRows
      .filter(r => r.type === 'violation')
      .map(r => r.key);
    setViolationState(prev => ({
      ...prev,
      selected: new Set(allIds)
    }));
  }, [flattenedRows]);

  const clearSelection = useCallback(() => {
    setViolationState(prev => ({
      ...prev,
      selected: new Set()
    }));
  }, []);

  const setSelected = useCallback((ids: Set<string>) => {
    setViolationState(prev => ({ ...prev, selected: ids }));
  }, []);

  // Export helpers
  const buildExportRows = useCallback((violationsToExport: Violation[]) => {
    return violationsToExport.map(v => ({
      'Activity ID': v.activity_id || '',
      'Activity Description/Name': v.activity_name || '',
      'Rule Name': v.rule_name || '',
      'Reason': v.message || '',
      'Severity': v.severity.charAt(0).toUpperCase() + v.severity.slice(1),
      'Fix Suggestion': v.fix_suggestion || '',
      'Requires Approval': v.requires_approval ? 'Yes' : 'No',
      'Approval Owner': v.approval_owner || ''
    }));
  }, []);

  const getExportData = useCallback((type: 'all' | 'filtered' | 'selected' | 'group', groupKey?: string) => {
    let violationsToExport: Violation[] = [];

    switch (type) {
      case 'all':
        violationsToExport = violations;
        break;
      case 'filtered':
        violationsToExport = sortedViolations;
        break;
      case 'selected':
        violationsToExport = sortedViolations.filter((v, idx) => {
          const id = getViolationId(v, idx);
          return violationState.selected.has(id);
        });
        break;
      case 'group':
        if (groupKey && groupedViolations[groupKey]) {
          violationsToExport = groupedViolations[groupKey];
        }
        break;
    }

    return buildExportRows(violationsToExport);
  }, [violations, sortedViolations, violationState.selected, groupedViolations, buildExportRows, getViolationId]);

  return {
    viewState,
    setViewState,
    violationState,
    filteredViolations,
    sortedViolations,
    groupedViolations,
    flattenedRows,
    uniqueSeverities,
    uniqueRules,
    toggleReviewed,
    toggleSelected,
    setSelected,
    bulkMarkReviewed,
    acceptRisk,
    selectAll,
    clearSelection,
    getExportData,
    getViolationId,
    reviewedCount: violationState.reviewed.size
  };
}
